import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertController, ToastController } from '@ionic/angular';
import { Navigate } from '@ngxs/router-plugin';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import {
  ConnectWebSocket,
  DisconnectWebSocket,
  WebSocketConnected,
  WebSocketDisconnected
} from '@ngxs/websocket-plugin';
import { GpAuthService, GpAuthState, isAuthRejected, ResetAuthState } from 'gp-auth';
import { jwtDecode } from 'jwt-decode';
import { addIcons } from 'ionicons';
import { alertCircleOutline, arrowBack, bookOutline, buildOutline, callOutline, camera, chatbubblesOutline, checkmarkDoneOutline, chevronDown, constructOutline, desktop, documentTextOutline, eyeOutline, home, homeOutline, image, logIn, logInOutline, logOut, logOutOutline, navigateOutline, personAdd, personAddOutline, personCircle, personCircleOutline, qrCodeOutline, receiptOutline, returnDownForwardOutline, save, send, settings, sparklesOutline, warningOutline } from 'ionicons/icons';
import { RtToastService, RtToastType } from 'rt-toasts';
import { BehaviorSubject, combineLatest, distinctUntilChanged, of, Subject, Subscription, switchMap, takeUntil, timer } from 'rxjs';
import { filter, first, map } from 'rxjs/operators';
import {
  AccessState,
  CheckVersionService,
  GetOrdersCount,
  GetStandsCount,
  LoadUserRoles,
  OrderListState,
  ProfileState,
  StandListState,
  UpdateProfile
} from 'shared';
import { PermissionEnum } from 'symbols';
import pkg from '../../package.json';
import { BUILD_ID } from '../build-id';
import { AppInfoReportService } from './app-info/app-info-report.service';
import { environment, settings as config} from '../environments/environment';
import { nativeWsBase } from './server-urls';
import { LiveUpdateService } from './live-update/live-update.service';
import { SoftphoneService } from './softphone/softphone.service';
import { IncomingCallService } from './softphone/incoming-call.service';
import { QuickDialService } from './softphone/quick-dial.service';
import { GpAsteriskService, GpAsteriskState, TypeCall, CallingState } from 'gp-asterisk';
import { CallDirectorySyncService } from './call-directory/call-directory-sync.service';
import { GpsService } from './gps/gps.service';
import { PushService } from './push/push.service';
import { routesMap } from './routes';

export const MESSAGES_COLOR_MAP = new Map([
  [RtToastType.Error, 'danger'],
  [RtToastType.Info, 'medium'],
  [RtToastType.Success, 'success'],
  [RtToastType.Warning, 'warning']
]);


@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  appVersion = pkg.version;
  // коротка позначка OTA-збірки — видно, чи вже працює нова версія
  buildLabel = BUILD_ID.slice(0, 8);

  private readonly appInfoReport = inject(AppInfoReportService);

  form = new FormGroup({
    avatar: new FormControl<File>(null)
  });

  readonly ordersCount$ = this.store.select(OrderListState.ordersCount);
  readonly standsCount$ = this.store.select(StandListState.standsCount);

  readonly access$ = this.store.select(AccessState.currentRolesDict);
  readonly currentUser$ = this.store.select(ProfileState.currentUser);
  readonly isAuthenticated$ = this.store.select(GpAuthState.isAuthenticated);
  protected readonly PermissionEnum = PermissionEnum;

  readonly hasDirectCalls$ = this.gpAsteriskService.hasActiveDirectCalls$;
  // лише ті вхідні, які ще ніхто не взяв (саме для них лунає сигнал); прийняті іншими операторами не показуємо,
  // інакше в робочий час картки постійно виїжджали й зникали поверх сторінки
  readonly incomingCalls$ = this.store.select(GpAsteriskState.callList).pipe(
    map(calls => calls.filter(c => c.type === TypeCall.InCall && c.status !== CallingState.Answered))
  );
  // картки вхідних, які оператор змахнув убік (не хоче відповідати й працює в застосунку): сигнал лунає далі,
  // зверху лишається маленька смужка, торкання повертає картки; новий дзвінок показується як звичайно
  private readonly hiddenCallIds$ = new BehaviorSubject<string[]>([]);
  readonly visibleCalls$ = combineLatest([this.incomingCalls$, this.hiddenCallIds$]).pipe(
    map(([calls, hidden]) => calls.filter(c => !hidden.includes(c.id)))
  );
  readonly hiddenCalls$ = combineLatest([this.incomingCalls$, this.hiddenCallIds$]).pipe(
    map(([calls, hidden]) => calls.filter(c => hidden.includes(c.id)))
  );

  // зелений, якщо номер зареєстрований: вбудованим телефоном у застосунку або стороннім SIP-клієнтом / телефоном
  readonly sipRegistered$ = combineLatest([
    this.currentUser$,
    this.store.select(GpAsteriskState.dongleStatus),
    this.softphoneService.state$,
  ]).pipe(
    map(([user, statuses, softphone]) => {
      if (softphone.status === 'registered') return true;
      if (!user?.code) return false;
      return statuses?.some(s =>
        (s.device === `SIP/${user.code}` || s.device === user.code) &&
        (s.status === 'Registered' || s.status === 'Reachable')
      ) ?? false;
    }),
    distinctUntilChanged(),
  );

  readonly routesMap = routesMap;
  subscription = new Subscription();
  private destroy$ = new Subject<void>();
  private wsUrl!: string;

  constructor(
    private toastController: ToastController,
    private actions$: Actions,
    private rtToastService: RtToastService,
    private store: Store,
    private router: Router,
    private pushService: PushService,
    private gpsService: GpsService,
    private checkVersionService: CheckVersionService,
    private alertController: AlertController,
    private gpAsteriskService: GpAsteriskService,
    private gpAuthService: GpAuthService,
    private callDirectorySyncService: CallDirectorySyncService,
    private liveUpdateService: LiveUpdateService,
    private softphoneService: SoftphoneService,
    private incomingCallService: IncomingCallService,
    private quickDialService: QuickDialService,
  ) {
    // якомога раніше: підтвердити, що ця версія коду стартувала (інакше OTA-плагін відкотить її)
    this.liveUpdateService.init();
    // віджет «Швидкий набір»: список обраних і дзвінки з нього
    this.quickDialService.init();
    this.initializeApp();
    addIcons({ alertCircleOutline, arrowBack, callOutline, chatbubblesOutline, bookOutline, buildOutline, camera, checkmarkDoneOutline, constructOutline, desktop, documentTextOutline, eyeOutline, home, homeOutline, image, logIn, logInOutline, logOut, logOutOutline, navigateOutline, personAdd, personAddOutline, personCircle, personCircleOutline, qrCodeOutline, receiptOutline, returnDownForwardOutline, save, send, settings, sparklesOutline, warningOutline });
  }

  initializeApp() {
    this.rtToastService.lastToast.pipe(filter(x => !!x), takeUntil(this.destroy$)).subscribe(toast =>
      this.toastController
        .create({
          message: toast.message,
          duration: toast.timeout || 2000,
          color: MESSAGES_COLOR_MAP.get(toast.type as RtToastType)
        })
        .then(toast => toast.present())
    );
  }

  ngOnInit(): void {
    // вбудований телефон: реєструємось після входу (якщо користувач увімкнув його), відключаємось після виходу
    this.isAuthenticated$.pipe(distinctUntilChanged(), takeUntil(this.destroy$)).subscribe(isAuth => {
      if (isAuth) {
        this.softphoneService.start();
        this.incomingCallService.start();
      } else {
        this.softphoneService.stop();
      }
    });

    this.access$.pipe(takeUntil(this.destroy$)).subscribe(access => {
      if (access[PermissionEnum.CAN_READ_ORDERS]) {
        this.store.dispatch(new GetOrdersCount());
      }

      if (access[PermissionEnum.CAN_READ_STANDS]) {
        this.store.dispatch(new GetStandsCount());
      }

      if (access[PermissionEnum.ACCESS_TO_SIP]) {
        this.gpAsteriskService.refreshDirectCallsStatus();
      }
    });

    this.isAuthenticated$.pipe(
      filter(isAuth => isAuth),
      first(),
      switchMap(() => this.pushService.fcmToken$.asObservable().pipe(
          filter(token => !!token),
          distinctUntilChanged()
        )
      )
    ).subscribe(() => {
      this.pushService.registerPush();
      this.gpsService.requestUseGpsPermission();
    });

    // Синхронізація CallDirectory (caller ID) при першому вході
    this.isAuthenticated$.pipe(
      filter(isAuth => isAuth),
      first(),
      switchMap(() => {
        const access = this.store.selectSnapshot(AccessState.currentRolesDict);
        if (!access[PermissionEnum.ACCESS_TO_CONTACT_IDENTIFICATION]) {
          return this.callDirectorySyncService.clear();
        }
        return this.callDirectorySyncService.sync();
      }),
      takeUntil(this.destroy$)
    ).subscribe();

    // яка версія застосунку стоїть у користувача — видно в адмінці «Користувачі»
    this.isAuthenticated$.pipe(
      distinctUntilChanged(),
      filter(isAuth => isAuth),
      takeUntil(this.destroy$)
    ).subscribe(() => this.appInfoReport.report());

    this.isAuthenticated$.pipe(
      filter(isAuth => isAuth),
      switchMap(() => this.pushService.fcmToken$.asObservable().pipe(filter(token => !!token))),
      takeUntil(this.destroy$)
    ).subscribe((token) => {
      this.pushService.saveToken(token).subscribe();
    });


    this.isAuthenticated$.pipe(first()).subscribe((isAuth) => {
      const hideSplash = () => {
        ['global-spinner', 'global-spinner__text', 'app-loading'].forEach(id => {
          const el = document.getElementById(id);
          if (el) el.style.display = 'none';
        });
        import('@capacitor/splash-screen').then(({ SplashScreen }) => SplashScreen.hide());
      };

      if (isAuth) {
        const token = this.store.selectSnapshot(GpAuthState.token);
        if (this.isTokenExpiredOrInvalid(token)) {
          const openDashboard = () => {
            if (this.router.url === '/') {
              this.store.dispatch(new Navigate([routesMap.dashboard.getAbsolutePath]));
            }
            hideSplash();
          };
          this.gpAuthService.refreshToken().subscribe({
            next: openDashboard,
            error: (err) => {
              // Мережа ще не піднялась чи сервер недоступний — сесію не чіпаємо, interceptor оновить токен пізніше
              if (!isAuthRejected(err)) return openDashboard();
              this.store.dispatch(new ResetAuthState());
              this.store.dispatch(new Navigate([routesMap.auth['login'].getAbsolutePath]));
              hideSplash();
            }
          });
        } else {
          if (this.router.url === '/') {
            this.store.dispatch(new Navigate([routesMap.dashboard.getAbsolutePath]));
          }
          hideSplash();
        }
      } else {
        this.store.dispatch(new Navigate([routesMap.auth['login'].getAbsolutePath]));
        hideSplash();
      }
    });

    document.addEventListener('resume', () => {
      const isAuth = this.store.selectSnapshot(GpAuthState.isAuthenticated);
      if (!isAuth) return;
      const token = this.store.selectSnapshot(GpAuthState.token);
      if (this.isTokenExpiredOrInvalid(token)) {
        this.gpAuthService.refreshToken().subscribe({
          error: (err) => {
            if (!isAuthRejected(err)) return;
            this.store.dispatch(new ResetAuthState());
            this.store.dispatch(new Navigate([routesMap.auth['login'].getAbsolutePath]));
          }
        });
      }
      this.appInfoReport.report();
      // Оновити ролі і синхронізувати caller ID
      this.store.dispatch(new LoadUserRoles()).subscribe(() => {
        const access = this.store.selectSnapshot(AccessState.currentRolesDict);
        if (access[PermissionEnum.ACCESS_TO_CONTACT_IDENTIFICATION]) {
          this.callDirectorySyncService.sync().subscribe();
        } else {
          this.callDirectorySyncService.clear().subscribe();
        }
      });
    });

    this.setupWebSocket();

  }


  hideCall(id: string): void {
    this.hiddenCallIds$.next([...this.hiddenCallIds$.value, id]);
  }

  showHiddenCalls(): void {
    this.hiddenCallIds$.next([]);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // === WS + версії ==========================================================

  private setupWebSocket() {
    // 1) зібрати URL (як у твоєму прикладі)
    const isHttps = window.location.protocol === 'https:';
    const protocol = isHttps ? 'wss://' : 'ws://';
    const isLocalhost = window.location.host.includes('localhost');
    const nativeWs = nativeWsBase();
    this.wsUrl = nativeWs ? `${nativeWs}/ws` : `${protocol}${isLocalhost ? 'localhost:4200' : window.location.host}/ws`;
    // 2) Стартове підключення, коли зʼявився користувач
    this.currentUser$
      .pipe(takeUntil(this.destroy$), distinctUntilChanged())
      .subscribe(user => {
        if (user) {
          this.dispatchConnect(); // важливо: новий інстанс дії щоразу
        } else {
          this.store.dispatch(new DisconnectWebSocket());
        }
      });

    // 3) На КОЖЕН успішний connect – перевірити версію
    this.actions$
      .pipe(ofActionDispatched(WebSocketConnected), takeUntil(this.destroy$))
      .subscribe(() => this.checkServerVersionOncePerValue());

    // 4) Після disconnect – спробувати реконект через 2с (лише якщо є користувач)
    combineLatest([
      this.actions$.pipe(ofActionDispatched(WebSocketDisconnected)),
      this.currentUser$
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([, user]) => {
        if (!user) return;
        timer(2000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.dispatchConnect());
      });
  }

  private dispatchConnect() {
    const token = this.store.selectSnapshot(GpAuthState.token);
    const url = token ? `${this.wsUrl}?token=${token}` : this.wsUrl;
    this.store.dispatch(new ConnectWebSocket({ url }));
  }

  private async checkServerVersionOncePerValue() {
    // нативний застосунок: код вбудований, перезавантаження нічого не дасть — оновлюємось через OTA у фоні
    if (this.liveUpdateService.isAvailable) {
      this.liveUpdateService.check();
      return;
    }
    this.checkVersionService.getVersions().pipe(first()).subscribe(async versions => {
      // показувати алерт лише якщо серверна версія відрізняється від поточної
      if (environment.production && versions?.mobile && versions.mobile !== BUILD_ID) {
        // не показувати повторно для тієї ж серверної версії (WS реконнект не має тригерити алерт знову)
        if (localStorage.getItem('prompted_for_version') === versions.mobile) return;
        localStorage.setItem('prompted_for_version', versions.mobile);

        const alert = await this.alertController.create({
          message: `Доступна нова версія додатку. Оновити?`,
          buttons: [
            {
              text: 'OK',
              handler: () => {
                // очищаємо запис щоб після перезавантаження нова версія вже не тригерила алерт
                localStorage.removeItem('prompted_for_version');
                import('@capacitor/splash-screen').then(({ SplashScreen }) => {
                  SplashScreen.show({ autoHide: false }).finally(() => {
                    // cache-bust: додаємо timestamp щоб браузер не повернув стару версію
                    window.location.href = window.location.pathname + '?v=' + Date.now();
                  });
                }).catch(() => {
                  window.location.href = window.location.pathname + '?v=' + Date.now();
                });
              }
            },
            { text: 'Відмінити' }
          ]
        });
        await alert.present();
      }
    });
  }

  // ==========================================================================

  private isTokenExpiredOrInvalid(token: string | null): boolean {
    if (!token) return true;
    try {
      const decoded = jwtDecode(token) as { exp?: number };
      return !decoded.exp || decoded.exp < Math.floor(Date.now() / 1000);
    } catch {
      return true;
    }
  }

  fileChangeEvent($event: File) {
    this.form.controls.avatar.setValue($event);
    this.store.dispatch(new UpdateProfile(this.form.value));
  }

  protected readonly config = config;
}
